import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link } from "react-router-dom";
import {
  FaCheck,
  FaChartBar,
  FaExclamationTriangle,
  FaUsers,
  FaClock,
  FaTasks,
  FaComments,
  FaArrowRight,
  FaTimesCircle,
} from "react-icons/fa";
import api from "../../services/api";
import "./admin.css";
import LiveLocationMap from "./LiveLocationMap";
import AdminAttendanceChart from "./AdminAttendanceChart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalInterns: 0,
    totalStudents: 0,
    totalMembers: 0,
    todayCheckedIn: 0,
    todayPending: 0,
    todayCompleted: 0,
    recentAttendances: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // ✅ Graph States
  const [employeesList, setEmployeesList] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [graphDays, setGraphDays] = useState(14);

  const getToken = () => localStorage.getItem("token");

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const currentTime = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const handleGetDashboardData = async () => {
    try {
      setIsLoading(true);
      const token = getToken();

      const empResponse = await api.get("/admin/get_employees", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const statusResponse = await api.get("/admin/get_today_status", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const attendanceResponse = await api.get("/admin/get_all_attendance", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (empResponse.status === 200 && statusResponse.status === 200) {
        const employees = empResponse.data.employees || [];
        const todayStatus = statusResponse.data.todayStatus || {};
        const attendances = attendanceResponse.data.attendances || [];

        setEmployeesList(employees);
        setAttendanceRecords(attendances);

        const activeEmployees = employees.filter((emp) => {
          const empStatus =
            emp.status !== undefined && emp.status !== null
              ? parseInt(emp.status)
              : 1;
          return empStatus === 1;
        });

        const totalEmployees = activeEmployees.filter(
          (emp) => emp.role === "employee"
        ).length;
        const totalInterns = activeEmployees.filter(
          (emp) => emp.role === "intern"
        ).length;
        const totalStudents = activeEmployees.filter(
          (emp) => emp.role === "student"
        ).length;

        let todayCheckedIn = 0;
        let todayCompleted = 0;

        Object.values(todayStatus).forEach((status) => {
          if (status.checked_out) {
            todayCompleted++;
          } else if (status.checked_in) {
            todayCheckedIn++;
          }
        });

        const totalMembers = totalEmployees + totalInterns + totalStudents;
        const todayPending = totalMembers - todayCheckedIn - todayCompleted;

        const recentAttendances = attendances.slice(0, 5);

        setStats({
          totalEmployees,
          totalStudents,
          totalInterns,
          totalMembers,
          todayCheckedIn,
          todayPending,
          todayCompleted,
          recentAttendances,
        });
      }
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message || "Failed to fetch dashboard data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Build graph data (skip Sundays, filter by role)
  const graphData = useMemo(() => {
    if (!employeesList.length) return [];

    const employeeRoleMap = {};
    const activeEmployeeIds = new Set();

    employeesList.forEach((emp) => {
      const empStatus =
        emp.status !== undefined && emp.status !== null
          ? parseInt(emp.status)
          : 1;
      if (empStatus === 1) {
        const role = String(emp.role || "employee").toLowerCase();
        employeeRoleMap[emp.id] = role;
        activeEmployeeIds.add(emp.id);
      }
    });

    // Build last N NON-SUNDAY days array (oldest -> newest)
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let offset = 0;
    while (days.length < graphDays) {
      const d = new Date(today);
      d.setDate(d.getDate() - offset);
      offset++;

      // ✅ Skip Sundays
      if (d.getDay() === 0) continue;

      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;

      days.push({
        dateStr,
        label: d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        dayName: d.toLocaleDateString("en-US", { weekday: "short" }),
      });
    }
    days.reverse(); // oldest -> newest

    // Group attendance by date
    const attendanceByDate = {};

    attendanceRecords.forEach((rec) => {
      if (!rec.check_in) return;
      const empId = rec.user_id || rec.employee_id || rec.employeeId;
      if (!empId) return;
      if (!activeEmployeeIds.has(empId)) return;

      const role = employeeRoleMap[empId];
      if (!role) return;

      if (roleFilter !== "all" && role !== roleFilter) return;

      const checkInDate = new Date(rec.check_in);
      const dateStr = `${checkInDate.getFullYear()}-${String(
        checkInDate.getMonth() + 1
      ).padStart(2, "0")}-${String(checkInDate.getDate()).padStart(2, "0")}`;

      if (!attendanceByDate[dateStr]) {
        attendanceByDate[dateStr] = new Set();
      }
      attendanceByDate[dateStr].add(empId);
    });

    // Total active members for current filter
    const totalForFilter = employeesList.filter((emp) => {
      const empStatus =
        emp.status !== undefined && emp.status !== null
          ? parseInt(emp.status)
          : 1;
      if (empStatus !== 1) return false;
      if (roleFilter === "all") return true;
      return String(emp.role || "").toLowerCase() === roleFilter;
    }).length;

    return days.map((d) => {
      const presentCount = attendanceByDate[d.dateStr]
        ? attendanceByDate[d.dateStr].size
        : 0;
      const absentCount = Math.max(0, totalForFilter - presentCount);
      return {
        date: d.label,
        dayName: d.dayName,
        Present: presentCount,
        Absent: absentCount,
        Total: totalForFilter,
      };
    });
  }, [employeesList, attendanceRecords, roleFilter, graphDays]);

  const formatTime = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    handleGetDashboardData();
  }, []);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const rate =
        data.Total > 0 ? Math.round((data.Present / data.Total) * 100) : 0;
      return (
        <div
          style={{
            background: "white",
            padding: "12px 16px",
            borderRadius: "10px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            border: "1px solid #e2e8f0",
            fontSize: "13px",
            minWidth: "160px",
          }}
        >
          <div
            style={{
              fontWeight: "700",
              color: "#1e293b",
              marginBottom: "8px",
              borderBottom: "1px solid #f1f5f9",
              paddingBottom: "6px",
            }}
          >
            {data.dayName}, {label}
          </div>
          <div
            style={{
              color: "#10b981",
              fontWeight: "600",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "4px",
            }}
          >
            <span><FaCheck style={{ marginRight: "6px" }} />Present</span>
            <span>{data.Present}</span>
          </div>
          <div
            style={{
              color: "#ef4444",
              fontWeight: "600",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "4px",
            }}
          >
            <span><FaTimesCircle style={{ marginRight: "6px" }} />Absent</span>
            <span>{data.Absent}</span>
          </div>
          <div
            style={{
              color: "#64748b",
              fontSize: "11px",
              marginTop: "6px",
              paddingTop: "6px",
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Total</span>
            <span style={{ fontWeight: "600" }}>{data.Total}</span>
          </div>
          <div
            style={{
              color: "#667eea",
              fontSize: "11px",
              marginTop: "2px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Attendance Rate</span>
            <span style={{ fontWeight: "700" }}>{rate}%</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="admin-container">
      {/* Header Banner */}
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title">Admin Dashboard</h1>
            <p className="header-subtitle">
              {currentDate} | {currentTime}
            </p>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="error-message">
          <FaTimesCircle style={{ marginRight: "6px" }} /> {errorMsg}
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "80px" }}>
          <div className="loading-spinner"></div>
          <p style={{ marginTop: "15px", color: "#888" }}>
            Loading dashboard...
          </p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card stat-primary">
              <div className="stat-icon"><FaUsers /></div>
              <div className="stat-info">
                <h3 className="stat-number">{stats.totalMembers}</h3>
                <p className="stat-label">Total Active Members</p>
              </div>
              <div className="stat-detail">
                <span>{stats.totalEmployees} Employees</span>
                <span>{stats.totalInterns} Interns</span>
                <span>{stats.totalStudents} Students</span>
              </div>
            </div>

            <div className="stat-card stat-success">
              <div className="stat-icon"><FaCheck /></div>
              <div className="stat-info">
                <h3 className="stat-number">{stats.todayCompleted}</h3>
                <p className="stat-label">Completed Today</p>
              </div>
            </div>

            <div className="stat-card stat-warning">
              <div className="stat-icon"><FaClock /></div>
              <div className="stat-info">
                <h3 className="stat-number">{stats.todayCheckedIn}</h3>
                <p className="stat-label">Checked In Now</p>
              </div>
            </div>

            <div className="stat-card stat-danger">
              <div className="stat-icon"><FaExclamationTriangle /></div>
              <div className="stat-info">
                <h3 className="stat-number">
                  {stats.todayPending > 0 ? stats.todayPending : 0}
                </h3>
                <p className="stat-label">Pending Today</p>
              </div>
            </div>
          </div>

          {/* ✅ Attendance Graph */}
          <div
            className="students-card"
            style={{ marginBottom: "20px", overflow: "hidden" }}
          >
            <div
              className="card-header-custom"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><FaChartBar /> Attendance Overview</h3>
                <p className="card-subtitle">
                  Daily present vs absent trends (Sundays excluded)
                </p>
              </div>
              {/* ✅ Filters in a row */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  flexWrap: "nowrap",
                }}
              >
                <select
                  className="form-input filter-select"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{
                    minWidth: "150px",
                    padding: "9px 12px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    fontWeight: "500",
                    fontSize: "13px",
                    color: "#1e293b",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All Roles</option>
                  <option value="employee">Employee</option>
                  <option value="intern">Intern</option>
                  <option value="student">Student</option>
                </select>
                <select
                  className="form-input filter-select"
                  value={graphDays}
                  onChange={(e) => setGraphDays(parseInt(e.target.value))}
                  style={{
                    minWidth: "150px",
                    padding: "9px 12px",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    fontWeight: "500",
                    fontSize: "13px",
                    color: "#1e293b",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value={7}>Last 7 Days</option>
                  <option value={14}>Last 14 Days</option>
                  <option value={30}>Last 30 Days</option>
                </select>
              </div>
            </div>

            <div className="card-body-custom">
              {/* Summary strip */}
              {graphData.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    marginBottom: "18px",
                    flexWrap: "wrap",
                  }}
                >
                  {(() => {
                    const totalPresent = graphData.reduce(
                      (sum, d) => sum + d.Present,
                      0
                    );
                    const totalAbsent = graphData.reduce(
                      (sum, d) => sum + d.Absent,
                      0
                    );
                    const avgPresent =
                      graphData.length > 0
                        ? Math.round(totalPresent / graphData.length)
                        : 0;
                    return (
                      <>
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                            border: "1px solid #86efac",
                            borderRadius: "10px",
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}><FaChartBar /></span>
                          <div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#065f46",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                              }}
                            >
                              Avg Present
                            </div>
                            <div
                              style={{
                                fontSize: "18px",
                                fontWeight: "800",
                                color: "#047857",
                                lineHeight: "1.1",
                              }}
                            >
                              {avgPresent}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                            border: "1px solid #86efac",
                            borderRadius: "10px",
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}><FaCheck /></span>
                          <div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#065f46",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                              }}
                            >
                              Total Present
                            </div>
                            <div
                              style={{
                                fontSize: "18px",
                                fontWeight: "800",
                                color: "#059669",
                                lineHeight: "1.1",
                              }}
                            >
                              {totalPresent}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #fef2f2, #fee2e2)",
                            border: "1px solid #fca5a5",
                            borderRadius: "10px",
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span style={{ fontSize: "20px" }}><FaExclamationTriangle /></span>
                          <div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#991b1b",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                              }}
                            >
                              Total Absent
                            </div>
                            <div
                              style={{
                                fontSize: "18px",
                                fontWeight: "800",
                                color: "#dc2626",
                                lineHeight: "1.1",
                              }}
                            >
                              {totalAbsent}
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {graphData.length > 0 ? (
                <div style={{ width: "100%", height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={graphData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                      barCategoryGap="25%"
                      barGap={4}
                    >
                      <defs>
                        <linearGradient
                          id="presentGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor="#34d399" />
                          <stop offset="100%" stopColor="#059669" />
                        </linearGradient>
                        <linearGradient
                          id="absentGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor="#f87171" />
                          <stop offset="100%" stopColor="#dc2626" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e2e8f0"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={{ stroke: "#cbd5e1" }}
                        tickLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: "#64748b" }}
                        axisLine={{ stroke: "#cbd5e1" }}
                        tickLine={false}
                      />
                      <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "rgba(102, 126, 234, 0.06)" }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: "13px", paddingTop: "10px" }}
                        iconType="circle"
                      />
                      <Bar
                        dataKey="Present"
                        fill="url(#presentGradient)"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={36}
                      />
                      <Bar
                        dataKey="Absent"
                        fill="url(#absentGradient)"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={36}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "60px" }}>
                  <div style={{ fontSize: "48px", marginBottom: "12px" }}>
                    <FaChartBar />
                  </div>
                  <p
                    style={{
                      color: "#888",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    No attendance data available
                  </p>
                </div>
              )}
            </div>
          </div>

          <AdminAttendanceChart/>
          <LiveLocationMap />

          {/* Today's Attendance Progress */}
          <div className="students-card mb-4">
            <div className="card-header-custom">
              <div>
                <h3 className="card-title">Today's Attendance Progress</h3>
                <p className="card-subtitle">
                  Real-time attendance tracking (Active Members Only)
                </p>
              </div>
            </div>
            <div className="card-body-custom">
              <div className="progress-container">
                <div className="progress-bar-wrapper">
                  <div className="progress-info">
                    <span>Completed</span>
                    <span>
                      {stats.todayCompleted} of {stats.totalMembers}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill progress-success"
                      style={{
                        width: `${
                          stats.totalMembers > 0
                            ? (stats.todayCompleted / stats.totalMembers) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="progress-bar-wrapper">
                  <div className="progress-info">
                    <span>Checked In</span>
                    <span>
                      {stats.todayCheckedIn} of {stats.totalMembers}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill progress-warning"
                      style={{
                        width: `${
                          stats.totalMembers > 0
                            ? (stats.todayCheckedIn / stats.totalMembers) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="progress-bar-wrapper">
                  <div className="progress-info">
                    <span>Pending</span>
                    <span>
                      {stats.todayPending > 0 ? stats.todayPending : 0} of{" "}
                      {stats.totalMembers}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill progress-danger"
                      style={{
                        width: `${
                          stats.totalMembers > 0
                            ? (stats.todayPending / stats.totalMembers) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions & Recent Activity */}
          <div className="dashboard-grid">
            {/* Quick Actions */}
            <div className="students-card">
              <div className="card-header-custom">
                <div>
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><FaTasks /> Quick Actions</h3>
                  <p className="card-subtitle">Frequently used actions</p>
                </div>
              </div>
              <div className="card-body-custom">
                <div className="quick-actions">
                  <Link to="/admin/employees" className="quick-action-btn">
                    <span className="quick-action-icon"><FaUsers /></span>
                    <div className="quick-action-text">
                      <strong>Manage Members</strong>
                      <small>Add, edit, or remove members</small>
                    </div>
                    <span className="quick-action-arrow">→</span>
                  </Link>

                  <Link to="/admin/attendance" className="quick-action-btn">
                    <span className="quick-action-icon"><FaChartBar /></span>
                    <div className="quick-action-text">
                      <strong>View Reports</strong>
                      <small>Attendance history & records</small>
                    </div>
                    <span className="quick-action-arrow">→</span>
                  </Link>

                  <Link to="/admin/tasks" className="quick-action-btn">
                    <span className="quick-action-icon"><FaTasks /></span>
                    <div className="quick-action-text">
                      <strong>Manage Tasks</strong>
                      <small>Assign, edit, delete & View Submissions</small>
                    </div>
                    <span className="quick-action-arrow">→</span>
                  </Link>

                  <Link to="/chat" className="quick-action-btn">
                    <span className="quick-action-icon"><FaComments /></span>
                    <div className="quick-action-text">
                      <strong>Chat With Members</strong>
                      <small>Chat with members</small>
                    </div>
                    <span className="quick-action-arrow">→</span>
                  </Link>

                  <Link to="/admin/teams" className="quick-action-btn">
                    <span className="quick-action-icon"><FaUsers /></span>
                    <div className="quick-action-text">
                      <strong>Manage Teams</strong>
                      <small>Add, edit, or remove team & their members</small>
                    </div>
                    <span className="quick-action-arrow">→</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="students-card">
              <div className="card-header-custom">
                <div>
                  <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}><FaClock /> Recent Activity</h3>
                  <p className="card-subtitle">Latest attendance records</p>
                </div>
              </div>
              <div className="card-body-custom">
                {stats.recentAttendances.length > 0 ? (
                  <div className="activity-list">
                    {stats.recentAttendances.map((record, index) => (
                      <div key={record.id || index} className="activity-item">
                        <div className="activity-avatar">
                          {record.employee_name?.charAt(0) || "E"}
                        </div>
                        <div className="activity-info">
                          <span className="activity-name">
                            {record.employee_name}
                          </span>
                          <span className="activity-time">
                            {formatTime(record.check_in)} -{" "}
                            {formatTime(record.check_out)}
                          </span>
                        </div>
                        <span
                          className={`status-badge status-${record.status?.toLowerCase() || "present"}`}
                        >
                          {record.status || "Present"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p className="empty-text">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;